// contextStore.js - Store pour gérer le contexte d'exécution de l'application
import { makeAutoObservable } from './mobx-config';
import { logger } from '../../utils/debug/logger.js';

/**
 * Store qui gère le contexte d'exécution de l'application
 */
class ContextStore {
  // Modes d'exécution possibles
  static MODES = {
    DIRECT: 'direct',    // Application en accès direct
    PROXY: 'proxy',      // Via reverse proxy
    IFRAME: 'iframe'     // Intégré dans un iframe
  };

  // État initial
  _mode = null;          // Mode d'exécution détecté
  _isInIframe = false;   // Flag pour iframe
  _isProxied = false;    // Flag pour proxy
  _basePath = '/';       // Chemin de base pour les ressources
  _origin = '';          // Origine de l'URL
  _urlParams = null;     // Paramètres d'URL parsés
  _partnerId = null;     // partnerId injecté par le gateway via cookie genealorama_partner
  _loginUrl = null;      // URL de retour partenaire injectée par le gateway
  _displayName = null;   // Nom de marque partenaire pour le tab title (white-label)

  constructor() {
    makeAutoObservable(this);
    this.detectContext();
  }

  /**
   * Lit un cookie par nom (cookies non-HttpOnly uniquement).
   * @private
   */
  _readCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Lit le cookie genealorama_partner posé par le gateway lors d'un /sso réussi.
   * Format : base64url(JSON.stringify({partnerId, loginUrl})). Pas signé,
   * informationnel uniquement — la vraie auth est dans genealorama_session.
   * @returns {{partnerId: string, loginUrl: string} | null}
   * @private
   */
  _readPartnerCookie() {
    const raw = this._readCookie('genealorama_partner');
    if (!raw) return null;
    try {
      const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '='.repeat((4 - b64.length % 4) % 4);
      return JSON.parse(atob(padded));
    } catch (e) {
      logger.initialization.context.warn('genealorama_partner cookie unreadable:', e);
      return null;
    }
  }

  /**
   * Détecte le contexte d'exécution et initialise le store
   */
  detectContext() {
    // Vérification iframe
    this._isInIframe = window.self !== window.top;

    // Informations sur l'URL courante
    const currentOrigin = window.location.origin;
    const currentPath = window.location.pathname;
    this._origin = currentOrigin;

    // Détection proxy : prioritairement via le cookie genealorama_partner injecté
    // par le gateway pour les partenaires custom hostname. Fallback sur l'ancienne
    // détection par hostname (familystory.live, genealogie.app) — transitoire,
    // sera retiré quand familystory.live aura migré vers le mode custom hostname.
    const partnerCookie = this._readPartnerCookie();
    if (partnerCookie) {
      this._isProxied = true;
      this._partnerId = partnerCookie.partnerId || null;
      this._loginUrl = partnerCookie.loginUrl || null;
      this._displayName = partnerCookie.displayName || null;
      // Adapter le tab title à la marque partenaire (white-label) si fournie.
      // Si vide → on garde la valeur par défaut du <title> (Genealorama).
      if (this._displayName) {
        try {
          document.title = this._displayName;
        } catch (e) {
          logger.initialization.context.warn('Failed to set document.title:', e);
        }
      }
    } else {
      this._isProxied = currentOrigin.includes('familystory.live') ||
                       (currentPath.includes('/genalogie.app/') && !currentPath.startsWith('/app/'));
    }
    
    // Détermination du mode principal
    this._mode = this._isInIframe 
               ? ContextStore.MODES.IFRAME 
               : (this._isProxied ? ContextStore.MODES.PROXY : ContextStore.MODES.DIRECT);
    
    // Configuration du chemin de base pour les ressources
    if (this._isProxied) {
      const pathSegments = currentPath.split('/');
      const appIndex = pathSegments.findIndex(segment => segment === 'app');
      
      if (appIndex >= 0) {
        this._basePath = pathSegments.slice(0, appIndex + 1).join('/') + '/';
      }
    }
    
    // Parse des paramètres d'URL
    this._urlParams = new URLSearchParams(window.location.search);
    
    logger.initialization.context.info(`🌍 Contexte d'application détecté: ${this._mode}`);
    logger.initialization.context.info(`📍 Chemin de base: ${this._basePath}`);
    
    // Pour la compatibilité avec le code existant (temporaire)
    this._setupCompatibility();
  }

  /**
   * Configure la compatibilité avec le code existant qui utilise window.APP_CONFIG
   * @private
   */
  _setupCompatibility() {
    // Maintenir la compatibilité avec le code existant
    window.APP_CONFIG = {
      isProxied: this._isProxied, // Séparation claire des concepts iframe vs proxy
      isInIframe: this._isInIframe,
      basePath: this._basePath,
      origin: this._origin,
      mode: this._mode
    };
    
    window.APP_CONTEXT = {
      isInIframe: this._isInIframe,
      isProxied: this._isProxied,
      mode: this._mode
    };
    
    // Configuration des fallbacks pour Workers si nécessaire
    if (this._isProxied || this._isInIframe) {
      const originalWorker = window.Worker;
      
      window.createWorkerWithFallback = (relativePath, options) => {
        try {
          return new originalWorker(relativePath, options);
        } catch (e) {
          logger.initialization.context.warn("Fallback pour Worker avec chemin relatif:", e);
          const correctedPath = `${this._basePath}${relativePath.replace(/^\.\.\//g, '')}`;
          logger.initialization.context.debug("Tentative avec chemin corrigé:", correctedPath);
          return new originalWorker(correctedPath, options);
        }
      };
    }
  }

  // Getters publics
  get mode() {
    return this._mode;
  }
  
  get isInIframe() {
    return this._isInIframe;
  }
  
  get isProxied() {
    return this._isProxied;
  }
  
  get basePath() {
    return this._basePath;
  }
  
  get origin() {
    return this._origin;
  }

  /**
   * URL vers laquelle rediriger l'utilisateur quand sa session Genealorama
   * expire ou quand il clique "Retour à l'accueil" en mode autonome.
   * Présent uniquement pour les partenaires custom hostname (cookie
   * genealorama_partner posé par le gateway).
   */
  get loginUrl() {
    return this._loginUrl;
  }

  /**
   * partnerId injecté par le gateway via cookie genealorama_partner.
   */
  get partnerId() {
    return this._partnerId;
  }

  /**
   * Nom de marque partenaire (white-label) injecté par le gateway via cookie.
   * null si pas de partenaire ou si le partenaire n'a pas configuré de displayName.
   */
  get displayName() {
    return this._displayName;
  }

  /**
   * Vérifie si un paramètre existe dans l'URL
   * @param {string} param Nom du paramètre
   * @returns {boolean} true si le paramètre existe
   */
  hasUrlParam(param) {
    return this._urlParams.has(param);
  }
  
  /**
   * Récupère la valeur d'un paramètre de l'URL
   * @param {string} param Nom du paramètre
   * @returns {string|null} Valeur du paramètre ou null
   */
  getUrlParam(param) {
    return this._urlParams.get(param);
  }
  
  /**
   * Vérifie si l'utilisateur est identifié via les paramètres d'URL (mode iframe)
   * @returns {boolean} true si les paramètres d'authentification sont présents
   */
  hasAuthParams() {
    return this.hasUrlParam('uid') && this.hasUrlParam('email');
  }
  
  /**
   * Récupère les informations d'authentification depuis l'URL (mode iframe)
   * @returns {Object|null} Infos d'authentification ou null
   */
  getAuthFromUrl() {
    if (!this.hasAuthParams()) return null;
    
    return {
      id: this.getUrlParam('uid'),
      email: this.getUrlParam('email')
    };
  }
}

// Export singleton
const contextStore = new ContextStore();
export default contextStore;